import { useEffect, useRef, useState } from 'react'
import RouteLink from './RouteLink'
import type { NavItem } from '../siteData'

interface SiteHeaderProps {
  currentPath: string
  navItems: NavItem[]
  onNavigate: (path: string) => void
}

export default function SiteHeader({
  currentPath,
  navItems,
  onNavigate,
}: SiteHeaderProps) {
  const [menuOpen, setMenuOpen] = useState(false)
  const [openMenu, setOpenMenu] = useState<string | null>(null)
  const headerRef = useRef<HTMLElement | null>(null)

  const handleNavigate = (path: string) => {
    setMenuOpen(false)
    setOpenMenu(null)
    onNavigate(path)
  }

  const toggleMenu = (label: string) => {
    setOpenMenu((value) => (value === label ? null : label))
  }

  useEffect(() => {
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null
      if (headerRef.current && target && !headerRef.current.contains(target)) {
        setOpenMenu(null)
      }
    }

    window.addEventListener('pointerdown', handlePointerDown)
    return () => window.removeEventListener('pointerdown', handlePointerDown)
  }, [])

  return (
    <header ref={headerRef} className="sticky top-0 z-50 border-b border-white/10 bg-charcoal/92 backdrop-blur-xl">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
        <RouteLink
          href="/"
          onNavigate={handleNavigate}
          className="max-w-[18rem] text-left"
        >
          <span className="block font-display text-[1.45rem] leading-none tracking-tight text-birch">
            Yethirihwanonhase
          </span>
        </RouteLink>

        <nav className="hidden items-center gap-1 lg:flex">
          {navItems.map((item) => {
            const isActive =
              currentPath === item.path ||
              item.children?.some((child) => child.path === currentPath)

            return (
              <div key={item.label} className="relative">
                {item.children ? (
                  <button
                    type="button"
                    className={`nav-link nav-link-button ${isActive ? 'nav-link-active' : ''}`}
                    onClick={() => toggleMenu(item.label)}
                    aria-expanded={openMenu === item.label}
                  >
                    {item.label}
                    <span className={`ml-2 text-[0.7rem] transition ${openMenu === item.label ? 'rotate-180' : ''}`}>
                      ▼
                    </span>
                  </button>
                ) : (
                  <RouteLink
                    href={item.path}
                    onNavigate={handleNavigate}
                    className={`nav-link ${isActive ? 'nav-link-active' : ''}`}
                  >
                    {item.label}
                  </RouteLink>
                )}
                {item.children ? (
                  <div
                    className={`absolute right-0 top-full min-w-72 pt-4 ${
                      openMenu === item.label ? 'block' : 'hidden'
                    }`}
                  >
                    <div className="rounded-[2rem] border border-white/20 bg-charcoal/95 backdrop-blur-xl shadow-xl space-y-2 p-3">
                      {item.children.map((child) => (
                        <RouteLink
                          key={child.path}
                          href={child.path}
                          onNavigate={handleNavigate}
                          className="block rounded-2xl px-4 py-3 text-sm transition hover:bg-white/5"
                        >
                          <span className="block font-medium text-white">{child.label}</span>
                          {child.description ? (
                            <span className="mt-1 block text-xs leading-5 text-white/80">
                              {child.description}
                            </span>
                          ) : null}
                        </RouteLink>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            )
          })}
        </nav>

        <div className="hidden lg:block">
          <RouteLink href="/contact" onNavigate={handleNavigate} className="btn-primary">
            Book a Consultation
          </RouteLink>
        </div>

        <button
          type="button"
          className="inline-flex items-center rounded-full border border-white/15 px-4 py-2 text-sm font-medium text-birch lg:hidden"
          onClick={() => setMenuOpen((value) => !value)}
          aria-expanded={menuOpen}
          aria-label="Toggle navigation"
        >
          Menu
        </button>
      </div>

      {menuOpen ? (
        <div className="border-t border-white/10 bg-charcoal/96 px-6 py-5 lg:hidden">
          <div className="mx-auto max-w-6xl space-y-3">
            {navItems.map((item) => (
              <div key={item.label} className="border-b border-white/8 pb-3">
                <div className="flex items-center justify-between">
                  <RouteLink
                    href={item.path}
                    onNavigate={handleNavigate}
                    className="block py-2 text-base font-medium text-birch"
                  >
                    {item.label}
                  </RouteLink>
                  {item.children ? (
                    <button
                      type="button"
                      className="rounded-full border border-white/10 px-3 py-1 text-xs uppercase tracking-[0.18em] text-weathered"
                      onClick={() => toggleMenu(item.label)}
                      aria-expanded={openMenu === item.label}
                    >
                      {openMenu === item.label ? 'Close' : 'Open'}
                    </button>
                  ) : null}
                </div>
                {item.children && openMenu === item.label ? (
                  <div className="mt-2 space-y-1 pl-3">
                    {item.children.map((child) => (
                      <RouteLink
                        key={child.path}
                        href={child.path}
                        onNavigate={handleNavigate}
                        className="block py-2 text-sm text-weathered"
                      >
                        {child.label}
                      </RouteLink>
                    ))}
                  </div>
                ) : null}
              </div>
            ))}
            <RouteLink href="/contact" onNavigate={handleNavigate} className="btn-primary mt-4 w-full">
              Book a Consultation
            </RouteLink>
          </div>
        </div>
      ) : null}
    </header>
  )
}
